import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { createFocusTrap } from 'focus-trap';

interface NodeTemplate {
  type: string;
  data: {
    label: string;
    [key: string]: any;
  };
}

interface NodeTemplatesModalProps {
  onClose: () => void;
  onApplyTemplate: (template: NodeTemplate) => void;
}

// Define node templates
const templates = {
  agent: [
    {
      type: 'agent',
      data: {
        label: 'Research Agent',
        role: 'Researcher',
        goals: 'Gather and analyze information\nProvide detailed reports',
        backstory: 'Expert in data analysis and research methodologies',
        allowDelegation: 'true',
        memory: '1000',
        llmModel: 'gpt-4-0125-preview',
      },
    },
    {
      type: 'agent',
      data: {
        label: 'Writer Agent',
        role: 'Content Creator',
        goals: 'Create engaging content\nMaintain consistent style',
        backstory: 'Experienced content writer with expertise in various topics',
        allowDelegation: 'false',
        memory: '2000',
        llmModel: 'gpt-4-0125-preview',
      },
    },
    {
      type: 'agent',
      data: {
        label: 'Analyst Agent',
        role: 'Data Analyst',
        goals: 'Process and analyze data\nGenerate insights and recommendations',
        backstory: 'Skilled data analyst with expertise in pattern recognition',
        allowDelegation: 'true',
        memory: '1500',
        llmModel: 'gpt-4-0125-preview',
      },
    },
  ],
  task: [
    {
      type: 'task',
      data: {
        label: 'Research Task',
        description: 'Conduct comprehensive research on the given topic',
        expectedOutput: 'Detailed research report with citations',
        priority: 'high',
        context: 'Focus on recent developments and reliable sources',
        executionOrder: '1',
      },
    },
    {
      type: 'task',
      data: {
        label: 'Content Creation',
        description: 'Create engaging content based on research findings',
        expectedOutput: 'Well-structured article or report',
        priority: 'medium',
        context: 'Target audience: technical professionals',
        executionOrder: '2',
      },
    },
    {
      type: 'task',
      data: {
        label: 'Data Analysis',
        description: 'Analyze data and identify key patterns',
        expectedOutput: 'Analysis report with visualizations',
        priority: 'high',
        context: 'Focus on statistical significance and trends',
        executionOrder: '1',
      },
    },
  ],
  tool: [
    {
      type: 'tool',
      data: {
        label: 'Web Scraper',
        toolType: 'FirecrawlScrapeWebsiteTool',
        configuration: JSON.stringify({
          selectors: ['.content', '.article', 'p'],
          timeout: 30,
        }, null, 2),
        description: 'Scrape web content with customizable settings',
        returnDirect: true,
      },
    },
    {
      type: 'tool',
      data: {
        label: 'Code Interpreter',
        toolType: 'CodeInterpreterTool',
        configuration: JSON.stringify({
          timeout: 60,
          memory_limit: '512M',
        }, null, 2),
        description: 'Execute Python code for data processing',
        returnDirect: false,
      },
    },
    {
      type: 'tool',
      data: {
        label: 'PDF Analyzer',
        toolType: 'PDFSearchTool',
        configuration: JSON.stringify({
          file_path: '',
          ocr_enabled: true,
        }, null, 2),
        description: 'Extract and analyze content from PDF documents',
        returnDirect: true,
      },
    },
  ],
  flow: [
    {
      type: 'flow',
      data: {
        label: 'Sequential Research',
        flowType: 'sequential',
        managerLLM: 'gpt-4-0125-preview',
        maxRetries: '3',
        timeout: '300',
      },
    },
    {
      type: 'flow',
      data: {
        label: 'Parallel Processing',
        flowType: 'parallel',
        managerLLM: 'gpt-4-0125-preview',
        maxRetries: '2',
        timeout: '600',
      },
    },
    {
      type: 'flow',
      data: {
        label: 'Conditional Analysis',
        flowType: 'conditional',
        managerLLM: 'gpt-4-0125-preview',
        condition: 'data.confidence_score > 0.8',
        maxRetries: '2',
        timeout: '300',
      },
    },
  ],
};

// Helper function to get template description
const getTemplateDescription = (template: NodeTemplate): string => {
  switch (template.type) {
    case 'agent':
      return template.data.role || '';
    case 'task':
      return template.data.description || '';
    case 'tool':
      return template.data.description || '';
    case 'flow':
      return `${template.data.flowType} flow with ${template.data.managerLLM}`;
    default:
      return '';
  }
};

export function NodeTemplatesModal({ onClose, onApplyTemplate }: NodeTemplatesModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isClosing, setIsClosing] = React.useState(false);

  useEffect(() => {
    // Add class to body when modal is open
    document.body.classList.add('modal-open');

    // Create focus trap
    const trap = createFocusTrap(modalRef.current!, {
      initialFocus: '#close-button',
      fallbackFocus: '.template-item',
      clickOutsideDeactivates: true,
      escapeDeactivates: true,
      onDeactivate: () => {
        setIsClosing(true);
        setTimeout(onClose, 200);
      }
    });

    // Activate trap after a short delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      trap.activate();
    }, 100);

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      trap.deactivate();
      document.body.classList.remove('modal-open');
    };
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[99999] overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4 text-center">
        <div className="fixed inset-0 bg-gray-900/80 transition-opacity" />

        <div 
          ref={modalRef}
          className={`relative transform overflow-hidden rounded-lg bg-gray-800 text-left shadow-xl transition-all w-full max-w-3xl ${
            isClosing ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
            <h2 className="text-lg font-medium text-gray-100">Node Templates</h2>
            <button 
              id="close-button"
              onClick={onClose}
              className="p-1 hover:bg-gray-700 rounded-md text-gray-400 hover:text-gray-300 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[calc(80vh-8rem)] overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Object.entries(templates).map(([category, categoryTemplates]) => (
                <div key={category} className="space-y-4">
                  <h3 className="text-base font-medium text-gray-300 capitalize">
                    {category} Templates
                  </h3>
                  <div className="space-y-3">
                    {categoryTemplates.map((template, index) => (
                      <button
                        key={index}
                        className="template-item w-full text-left p-4 bg-gray-800/50 border border-gray-700/50 rounded-lg hover:bg-gray-700/70 transition-all shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        onClick={() => onApplyTemplate(template)}
                      >
                        <h4 className="text-sm font-medium text-gray-100">
                          {template.data.label}
                        </h4>
                        <p className="text-xs text-gray-400 mt-1.5">
                          {getTemplateDescription(template)}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}